import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ArrowLeft, Download, Trash2, ImageOff, Loader2, Wand2 } from "lucide-react";

const absUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function PhotosPage() {
  const navigate = useNavigate();
  const { user, isPro } = useAuth();
  const pro = isPro || user?.role === "owner" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!pro) { setLoading(false); return; }
    api.get("/photos/history").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [pro]);

  const download = async (item) => {
    setBusy(item.id);
    try {
      const res = await fetch(absUrl(item.url), { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `aiBLACK-photo-${item.id.slice(0, 6)}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
    finally { setBusy(""); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/photos/history/${id}`);
      setItems((x) => x.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="photos-page">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="photos-back" onClick={() => navigate("/app")} className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors"><ArrowLeft size={18} /> Back</button>
          <Logo size="text-xl" />
          <div className="w-12" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3"><Wand2 size={14} /> Your photos</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mb-2">Your saved photos</h1>
        <p className="text-[#9ca3af] mb-8 max-w-xl">Every photo you generate, enhance or edit in chat is saved here — revisit and re-download anytime. <span className="text-[#FF5A00] font-semibold">Photos are kept for 7 days, then automatically removed</span> — download any you'd like to keep.</p>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={26} className="animate-spin text-[#FF5A00]" /></div>
        ) : !pro ? (
          <div className="bg-[#15151d] border border-[#2a3040] rounded-2xl p-8 text-center" data-testid="photos-upsell">
            <p className="text-[#9ca3af] mb-4">Photo tools are a Pro feature.</p>
            <button data-testid="photos-upgrade" onClick={() => navigate("/app")} className="bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-6 py-3 hover:bg-[#FF7A33] transition-colors">Upgrade to Pro</button>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-[#15151d] border border-[#2a3040] rounded-2xl p-12 text-center" data-testid="photos-empty">
            <ImageOff size={32} className="text-[#6b7280] mx-auto mb-3" />
            <p className="text-[#9ca3af]">No photos yet. Ask aiBLACK to create an image, or attach a photo and tap an enhance action — then save it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="photos-grid">
            {items.map((item) => (
              <div key={item.id} data-testid={`photo-item-${item.id}`} className="group relative rounded-xl overflow-hidden border border-[#2a3040] bg-[#15151d]">
                <img src={absUrl(item.url)} alt="Edited" className="w-full aspect-square object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-2 flex items-center justify-end gap-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`photo-download-${item.id}`} onClick={() => download(item)} disabled={busy === item.id}
                    className="w-9 h-9 rounded-full bg-[#FF5A00] text-[#0a0a0f] flex items-center justify-center hover:bg-[#FF7A33] transition-colors" title="Download">
                    {busy === item.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  </button>
                  <button data-testid={`photo-delete-${item.id}`} onClick={() => remove(item.id)}
                    className="w-9 h-9 rounded-full bg-[#0a0a0f]/80 border border-[#2a3040] text-white flex items-center justify-center hover:text-[#f87171] transition-colors" title="Delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
